/**
 * @fileoverview HTTP client with interceptors for API communication
 * @module services/api
 */

import Swal from 'sweetalert2';

/**
 * Get authentication token from localStorage
 * @returns {string} Decoded token
 */
const getAuthToken = () => {
  const tokens = localStorage.getItem('cm-acc-tk') || '';
  return tokens ? atob(tokens) : '';
};
const getAuthCompany = () => {
  let company = localStorage.getItem('cm-company') || '';
 company = company ? JSON.parse(company) : '';
//  console.log("company:",company)
  return company?.id || '';
};

/**
 * Show alert dialog using SweetAlert2
 * @param {string} url - URL to redirect after alert
 * @param {string} icon - Alert icon type
 * @param {string} title - Alert title
 * @param {string} detail - Alert message
 * @param {string} confirm - Confirm button text
 * @param {string} cancel - Cancel button text
 * @returns {Promise}
 */
export const showAlert = (url, icon, title, detail, confirm, cancel) => {
  const modals = document.getElementsByClassName('swal2-modal');
  const icons = document.getElementsByClassName('swal2-icon');
  const hasBangIcon = Array.from(icons).some((el) => el.textContent.trim() === '!');

  if (modals.length >= 2 || hasBangIcon) return;

  return Swal.fire({
    icon: icon || 'warning',
    title: title || (icon === 'success' ? 'Success' : 'Warning'),
    text: detail || (icon === 'success' ? 'The transaction was completed successfully.' : ' '),
    confirmButtonText: confirm || 'Confirm',
    showCancelButton: !!cancel,
    cancelButtonText: cancel || 'Cancel',
    background: '#fff',
    color: '#000000',
    confirmButtonColor: '#212529',
    cancelButtonColor: 'unset',
  }).then(({ isConfirmed }) => {
    if (url) {
      if (url === '#') window.location.reload();
      else window.location.href = url;
    }
    return isConfirmed;
  });
};

/**
 * Show/hide loading spinner
 * @param {boolean} status - Show or hide loading
 */
export const loading = (status) => {
  try {
    const loadingEl = document.getElementById('loading');
    if (status === true) {
      loadingEl.classList.remove('d-none');
    } else if (status === false) {
      loadingEl.classList.add('d-none');
    }
  } catch (e) {}
};

/**
 * Remove empty strings and null values from objects/arrays recursively
 * @param {*} value - The value to clean
 * @returns {*} Cleaned value
 */
// export const removeEmptyString = (value) => {
//   if (Array.isArray(value)) {
//     const arr = value.map(removeEmptyString).filter((v) => v !== '' && v !== null);
//     return arr.length > 0 ? arr : null;
//   }

//   if (value && typeof value === 'object') {
//     const obj = Object.fromEntries(
//       Object.entries(value)
//         .map(([k, v]) => [k, removeEmptyString(v)])
//         .filter(([_, v]) => v !== '' && v !== null)
//     );
//     return Object.keys(obj).length > 0 ? obj : null;
//   }

//   return value;
// };
export const removeEmptyString = (value) => {
  // ⭐ แปลง "-" เป็น "" แต่ไม่ให้ถูกลบ
  if (value === '-') {
    return '__EMPTY__'; // ใช้ placeholder กันโดน filter
  }

  if (Array.isArray(value)) {
    const arr = value
      .map(removeEmptyString)
      .filter((v) => v !== '' && v !== null);
    
    return arr.map(v => v === '__EMPTY__' ? '' : v);
  }

  if (value && typeof value === 'object') {
    const obj = Object.fromEntries(
      Object.entries(value)
        .map(([k, v]) => [k, removeEmptyString(v)])
        .filter(([_, v]) => v !== '' && v !== null)
    );

    // แปลง placeholder กลับ
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        k,
        v === '__EMPTY__' ? '' : v
      ])
    );
  }

  return value;
};

/**
 * Extract error message from API response
 * @param {Object} result - API response
 * @returns {string} Error message
 */
export const ERROR = (result) => {
  loading(false);
  if (!result) return '';
  if (result.detail) return result.detail;
  if (result.message) return result.message;
  if (result.data) {
    if (result.data.message) return result.data.message;
    return result.data;
  }
  return result.message || '';
};

/**
 * Build API URL
 * @param {string} url - Endpoint URL
 * @param {Object} params - Query parameters
 * @returns {string} Full URL
 */
const buildUrl = (url, params = null) => {
  let fullUrl = url.includes('://') ? url : (process.env.REACT_APP_API_URL + url).replaceAll(' ', '');

  if (params) {
    const queryString = Object.entries(params)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
    fullUrl += '?' + queryString;
  }

  return fullUrl;
};

/**
 * Handle API errors
 * @param {Object} result - API response
 * @param {Object} options - Options
 * @returns {Object} Processed response
 */
const handleResponse = (result, options = {}) => {
  if (result.status === true) return result;

  const message = ERROR(result);

  if (message === 'Token not found' || message === 'Token has expired') {
    localStorage.clear();
    showAlert('/', 'warning', 'Warning', message);
    return { status: false, message };
  }

  if (options.skipUserVerificationAlert && message === 'User not verified, please verify your account first') {
    return { status: false, message };
  }

  if (message === 'fetch is not defined') {
    showAlert('', 'warning', 'Warning', 'Data not found');
    return { status: false, message };
  }

  showAlert('', 'warning', 'Warning', message);
  return { status: false, message };
};

/**
 * Perform GET request
 * @param {string} url - API endpoint
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} API response
 */
export const GET = async (url, params, company_uid = true) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url,params);
    const companyUid = getAuthCompany(); // ดึงค่ามาก่อน

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    // ตรวจสอบว่าถ้ามีค่าค่อยใส่เข้าไป (ถ้าเป็น null/undefined/ว่าง จะไม่ใส่)
    if (companyUid && company_uid) {
      headers['company-uid'] = companyUid;
    }

    fetch(apiUrl, {
      method: 'GET',
      headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * Perform POST request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const POST = async (url, body, company_uid = true) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);
    const companyUid = getAuthCompany(); // ดึงค่ามาก่อน

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    // ตรวจสอบว่าถ้ามีค่าค่อยใส่เข้าไป (ถ้าเป็น null/undefined/ว่าง จะไม่ใส่)
    if (companyUid && company_uid) {
      headers['company-uid'] = companyUid;
    }

    fetch(apiUrl, {
      method: 'POST',
      headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
      body: JSON.stringify(removeEmptyString(body)),
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result, { skipUserVerificationAlert: true })))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * Perform PUT request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const PUT = async (url, body, company_uid = true) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);
    const companyUid = getAuthCompany(); // ดึงค่ามาก่อน

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    // ตรวจสอบว่าถ้ามีค่าค่อยใส่เข้าไป (ถ้าเป็น null/undefined/ว่าง จะไม่ใส่)
    if (companyUid && company_uid) {
      headers['company-uid'] = companyUid;
    }

    fetch(apiUrl, {
      method: 'PUT',
        headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
      body: JSON.stringify(removeEmptyString(body)),
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * Perform DELETE request
 * @param {string} url - API endpoint
 * @param {Object} body - Request body
 * @returns {Promise<Object>} API response
 */
export const DELETE = async (url, body, company_uid = true) => {
  return new Promise((resolve) => {
    const apiUrl = buildUrl(url);
    const companyUid = getAuthCompany(); // ดึงค่ามาก่อน

    // สร้าง Header พื้นฐาน
    const headers = {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: getAuthToken(),
    };

    // ตรวจสอบว่าถ้ามีค่าค่อยใส่เข้าไป (ถ้าเป็น null/undefined/ว่าง จะไม่ใส่)
    if (companyUid && company_uid) {
      headers['company-uid'] = companyUid;
    }

    fetch(apiUrl, {
      method: 'DELETE',
      headers: headers, // ใช้ header ที่เตรียมไว้
      cache: 'no-cache',
    })
      .then((result) => result.json())
      .then((result) => resolve(handleResponse(result)))
      .catch(() => resolve({ status: false, message: 'Error' }));
  });
};

/**
 * ขนาดไฟล์สูงสุดที่อนุญาตให้อัปโหลดฝั่ง client (หน่วย: byte)
 * ใช้ตรวจก่อนอัปโหลดเพื่อกัน error 413 และให้ผู้ใช้เห็น message ที่เข้าใจง่าย
 */
export const MAX_UPLOAD_SIZE = {
  image: 15 * 1024 * 1024, // 15MB
  video: 100 * 1024 * 1024, // 100MB
  default: 50 * 1024 * 1024, // 50MB
};

/**
 * อ่านไฟล์เป็น base64 (data URL) สำหรับ fallback path เดิม
 * @param {File} file
 * @returns {Promise<string>}
 */
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('read file failed'));
    reader.readAsDataURL(file);
  });

/**
 * อัปโหลดไฟล์ขึ้น S3
 * 1) พยายามใช้ presigned URL (POST /auth/upload/presign) แล้ว PUT ไฟล์ตรงเข้า S3
 *    -> ไม่ผ่าน body ของ API จึงไม่ติด limit ของ proxy/express (เลี่ยง 413 ถาวร)
 * 2) ถ้า presign ใช้ไม่ได้ (เช่น endpoint ยังไม่ deploy หรือ bucket ยังไม่ตั้ง CORS)
 *    -> fallback ไปใช้ base64 ผ่าน POST /auth/upload ของเดิม (ใช้งานได้เหมือนเดิมทุกประการ)
 *
 * @param {File} file
 * @param {{ folder?: string }} options
 * @returns {Promise<{status: boolean, data?: {file_path, file_name, mime_type, file_size}, message?: string}>}
 */
export const UPLOAD = async (file, { folder } = {}) => {
  if (!file) return { status: false, message: 'No file' };

  // ---- 1) Presigned URL (ยิงตรงเข้า S3) ----
  try {
    const presign = await POST('auth/upload/presign', {
      file_name: file.name,
      mime_type: file.type || undefined,
      folder,
      file_size: file.size,
    });

    if (presign?.status === true && presign?.data?.upload_url) {
      const { upload_url, public_url, mime_type } = presign.data;
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': mime_type || file.type || 'application/octet-stream' },
        body: file,
      });

      if (putRes.ok) {
        return {
          status: true,
          data: {
            file_path: public_url,
            file_name: file.name || null,
            mime_type: mime_type || file.type || null,
            file_size: file.size ?? null,
          },
        };
      }
      // presign สำเร็จแต่ PUT พลาด (เช่น CORS) -> ตกไปใช้ fallback ด้านล่าง
    }
  } catch (e) {
    // เงียบไว้ แล้วไป fallback
  }

  // ---- 2) Fallback: base64 ผ่าน /auth/upload (ทางเดิม) ----
  try {
    const base64 = await fileToBase64(file);
    const res = await POST('auth/upload', { file: base64, file_name: file.name, folder });
    if (res?.status === true && res?.data?.path) {
      return {
        status: true,
        data: {
          file_path: res.data.path,
          file_name: file.name || res.data.name || null,
          mime_type: res.data.mime_type || file.type || null,
          file_size: res.data.size ?? file.size ?? null,
        },
      };
    }
    return { status: false, message: res?.message };
  } catch (e) {
    return { status: false, message: 'upload failed' };
  }
};

const api = {
  GET,
  POST,
  PUT,
  DELETE,
  UPLOAD,
  ERROR,
  loading,
  showAlert,
  removeEmptyString,
  MAX_UPLOAD_SIZE,
};

export default api;
